/**
 * Formatting helpers for the free-text phone inputs in the GIS intake and
 * public order-lookup funnels, where the visitor is expected to type a full
 * international number including the country calling code.
 *
 * Imported from `libphonenumber-js/max` rather than the package root so the
 * metadata is shared with `usePhoneValidation` in `@natera/platform`, which
 * validates these same fields. Mixing subpaths would bundle a second copy of
 * the metadata, and the root's smaller set can't tell number types apart, so
 * formatting and validation could disagree on the same input.
 */

import {
  AsYouType,
  CountryCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js/max";

// Matches the default region used by `usePhoneValidation`, so a number this
// module accepts on paste is one that hook will also validate.
const DEFAULT_COUNTRY: CountryCode = "US";

// E.164 permits a maximum of 15 digits, including the country calling code.
const MAX_PHONE_DIGITS = 15;

/** Reduces a display-formatted number to the digits-and-plus form we persist. */
export const stripPhoneFormatting = (phone: string): string =>
  phone.replace(/[^\d+]/g, "");

/**
 * Formats partial input for display as the visitor types, grouping digits the
 * way libphonenumber does for the country implied by the calling code.
 *
 * Digits are always read as international (a leading `+` is implied) because
 * these fields ask for the country code — otherwise a US visitor typing
 * `2015550123` would be grouped as if `20` were a calling code. Separators are
 * dashes rather than libphonenumber's spaces to match the field placeholders
 * and the rest of the funnel.
 */
export const formatPhoneNumberInput = (input: string): string => {
  const digits = input.replace(/\D/g, "").slice(0, MAX_PHONE_DIGITS);
  if (!digits) return input.includes("+") ? "+" : "";

  return new AsYouType().input(`+${digits}`).replace(/ /g, "-");
};

/**
 * Normalizes pasted text to the display format, accepting the shapes people
 * actually copy — `(555) 123-4567`, `555.123.4567`, `+44 20 7123 1234` — and
 * inferring the calling code from {@link DEFAULT_COUNTRY} when it's absent.
 *
 * Only a possible-length parse is trusted, because parsing assumes the default
 * region for anything without a `+`: it reads a bare `442071231234` as a US
 * number and prefixes a second calling code. Falling back to the raw text
 * treats those as already-international, which is what they are.
 *
 * Numbers that fail full validation are still formatted, so the visitor sees
 * what landed in the field and gets the field's own validation message rather
 * than a silently emptied input.
 */
export const formatPastedPhoneNumber = (pasted: string): string => {
  const parsed = parsePhoneNumberFromString(pasted, DEFAULT_COUNTRY);
  const normalized = parsed?.isPossible() ? parsed.number : pasted;

  return formatPhoneNumberInput(normalized);
};
